// src/utils/leadTrackerExcel.js
// Fills the blank copy of Henry's coach workbook (public/lead-tracker-
// template.xlsx) with derived tracker rows + goals. The workbook's own
// formulas compute all metrics, quarters and ANNUAL METRICS — we write ONLY
// month data rows (E2:P51), the static month goal cells, and the NAME/YEAR
// labels. Template capacity is 50 rows per month; overflow is reported so the
// UI can warn loudly (screen and PDF always show everything).
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { MONTH_LABELS, rowsForMonth, UNASSIGNED_BUCKET } from "./leadTracker";

const TEMPLATE_URL = `${process.env.PUBLIC_URL || ""}/lead-tracker-template.xlsx`;
const DATA_COLS = ["E", "F", "G", "H", "I", "J", "K", "L", "M", "N", "O", "P"];
export const MONTH_SHEET_CAPACITY = 50;

// The lead's LOCAL calendar day as a UTC-midnight Date. ExcelJS converts
// dates to serials via UTC, so writing the raw timestamp slid an 8pm lead to
// the next day's cell (Henry, 2026-09-01: dates were wrong/garbled).
function localDayAsUtcDate(ms) {
  const d = new Date(ms);
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
}

// One workbook row (E..P) per tracker row. Pure — unit tested.
export function buildMonthSheetRows(rows, year, monthIndex) {
  return rowsForMonth(rows, year, monthIndex)
    .slice()
    .sort((a, b) => a.createdAt - b.createdAt)
    .map((r) => [
      localDayAsUtcDate(r.createdAt),
      r.name,
      r.leadType === "buyer" ? "Buyer" : "Seller",
      r.callOrAppt === "appt" ? "Appt" : "Call",
      r.contract ? "Yes" : "",
      r.preApproved ? "Yes" : "",
      r.closedYear === year ? r.closedLabel : "",
      r.referredByName || "",
      r.bucket === UNASSIGNED_BUCKET ? "" : r.bucket,
      r.phone || "",
      r.email || "",
      r.notes || "",
    ]);
}

// Write one month's data rows into a worksheet. The date cell gets an
// EXPLICIT date format: relying on the template's blank-cell styling turned
// out to be unreliable — rows past the first read back with no format, so
// Excel showed raw serial numbers like 46267.05 instead of dates (Henry,
// 2026-09-01: "the dates dotn come out"). Exported for unit tests.
export function applyMonthRows(ws, data) {
  data.forEach((vals, i) => {
    const rowNum = 2 + i;
    DATA_COLS.forEach((col, ci) => {
      const cell = ws.getCell(`${col}${rowNum}`);
      cell.value = vals[ci];
      if (col === "E") cell.numFmt = "mm-dd-yy";
    });
  });
}

export async function exportLeadTrackerExcel({ rows, goals, ownerName, year }) {
  const res = await fetch(TEMPLATE_URL);
  if (!res.ok) {
    throw new Error(`Lead Tracker template not found (${res.status})`);
  }
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(await res.arrayBuffer());
  const truncatedMonths = [];
  MONTH_LABELS.forEach((label, mi) => {
    const ws = wb.getWorksheet(label);
    if (!ws) return;
    const data = buildMonthSheetRows(rows, year, mi);
    if (data.length > MONTH_SHEET_CAPACITY) truncatedMonths.push(label);
    applyMonthRows(ws, data.slice(0, MONTH_SHEET_CAPACITY));
    ws.getCell("B8").value = goals.monthlyLeadsGoal;
    ws.getCell("B19").value = goals.closingsGoal;
    ws.getCell("C3").value = goals.leadToApptPct;
    ws.getCell("C5").value = goals.apptToContractPct;
    ws.getCell("C6").value = goals.contractToClosePct;
  });
  const annual = wb.getWorksheet("ANNUAL METRICS");
  if (annual) {
    annual.getCell("A2").value = `NAME: ${ownerName}`;
    annual.getCell("M2").value = `YEAR: ${year}`;
  }
  const buffer = await wb.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), `Lead Tracker ${year} - ${ownerName}.xlsx`);
  return { truncatedMonths };
}
